import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Popper from '@mui/material/Popper';
import type { SxProps, Theme } from '@mui/material/styles';
import { type UseAutocompleteReturnValue } from '@mui/material/useAutocomplete';
import {
	Fragment,
	HTMLAttributes,
	ReactNode,
	useLayoutEffect,
	useState
} from 'react';

import { POPPER_Z_INDEX } from '@/core/constants/layout';
import { mergeSx } from '@/utils/mergeSx';

import { DropdownItemBase } from '../autocomplete.types';

import { AutocompleteDropdownItem } from './AutocompleteDropdownItem/AutocompleteDropdownItem';
import { AutocompleteDropdownNoOptions } from './AutocompleteDropdownNoOptions/AutocompleteDropdownNoOptions';
import { autocompleteDropdownOptionsFilterStyles as sx } from './AutocompleteDropdownOptions.styles';

type AutoReturn<T> = UseAutocompleteReturnValue<T, false>;

type Props<T> = {
	open: boolean;
	loading?: boolean;
	query?: string;
	isAsync?: boolean;
	getListboxProps: AutoReturn<T>['getListboxProps'];
	getOptionProps: AutoReturn<T>['getOptionProps'];
	renderOption?: (
		option: T,
		optionProps: HTMLAttributes<HTMLLIElement>
	) => ReactNode;
	groupedOptions: AutoReturn<T>['groupedOptions'];
	options?: Array<T> | null;
	anchorEl: HTMLElement | null;
	customSx?: SxProps<Theme>;
	isTopOption?: (option: T) => boolean;
	sectionLabels?: { top: string; all: string };
};

const buildSectionLabelsByIndex = <T extends DropdownItemBase>(
	groupedOptions: readonly T[],
	isTopOption?: (option: T) => boolean,
	sectionLabels?: { top: string; all: string }
): Map<number, string> => {
	const labelsByIndex = new Map<number, string>();
	if (!isTopOption || !sectionLabels) return labelsByIndex;

	const seenTopIds = new Set<string>();
	let hasTopSection = false;
	let allSectionIndex: number | undefined;

	groupedOptions.forEach((opt, idx) => {
		const isFirstTopOccurrence = isTopOption(opt) && !seenTopIds.has(opt.id);

		if (isFirstTopOccurrence) {
			seenTopIds.add(opt.id);
			if (idx === 0) hasTopSection = true;
			return;
		}

		if (allSectionIndex === undefined) allSectionIndex = idx;
	});

	if (hasTopSection && allSectionIndex !== undefined) {
		labelsByIndex.set(0, sectionLabels.top);
		labelsByIndex.set(allSectionIndex, sectionLabels.all);
	}

	return labelsByIndex;
};

export const AutocompleteDropdownOptions = <
	T extends DropdownItemBase = DropdownItemBase
>({
	open,
	loading,
	query,
	isAsync,
	getListboxProps,
	getOptionProps,
	renderOption,
	groupedOptions,
	options,
	anchorEl,
	customSx,
	isTopOption,
	sectionLabels
}: Props<T>) => {
	const [popperWidth, setPopperWidth] = useState(anchorEl?.clientWidth ?? 200);

	useLayoutEffect(() => {
		if (!anchorEl) return;

		const observer = new ResizeObserver(entries => {
			setPopperWidth(entries[0].contentRect.width);
		});
		observer.observe(anchorEl);

		return () => {
			observer.disconnect();
		};
	}, [anchorEl]);

	if (!isAsync && !groupedOptions.length) return null;

	const sectionLabelsByIndex = buildSectionLabelsByIndex(
		groupedOptions as T[],
		isTopOption,
		sectionLabels
	);

	return (
		<Popper
			open={open}
			anchorEl={anchorEl}
			placement='bottom-start'
			sx={{
				width: popperWidth,
				zIndex: POPPER_Z_INDEX
			}}
		>
			<Box
				component='ul'
				{...getListboxProps()}
				sx={mergeSx(sx.dropdown, customSx)}
			>
				{loading && <LinearProgress sx={sx.linearProgress} />}

				{isAsync &&
					options !== null &&
					groupedOptions.length === 0 &&
					query &&
					query.length > 0 && <AutocompleteDropdownNoOptions query={query} />}

				{groupedOptions.map((opt, idx) => {
					const { key: _muiKey, ...optionProps } = getOptionProps({
						option: opt as T,
						index: idx
					});
					const sectionLabel = sectionLabelsByIndex.get(idx);

					return (
						<Fragment key={`option-${idx}-${opt.id}`}>
							{sectionLabel && (
								<Box
									component='li'
									aria-hidden
									sx={mergeSx(
										sx.sectionLabel,
										idx !== 0 && sx.secondSectionLabel
									)}
								>
									{sectionLabel}
								</Box>
							)}
							{sectionLabel && (
								<Divider component='li' aria-hidden sx={sx.sectionDivider} />
							)}
							{renderOption ? (
								<Box component='li' {...optionProps}>
									{renderOption(opt as T, optionProps)}
								</Box>
							) : (
								<AutocompleteDropdownItem
									optionProps={optionProps}
									item={opt}
								/>
							)}
						</Fragment>
					);
				})}
			</Box>
		</Popper>
	);
};
